import React, { useEffect, useRef, useCallback } from 'react';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/**
 * Renders Google's own "Sign in with Google" button via Google Identity
 * Services (loaded in public/index.html). Renders nothing if
 * REACT_APP_GOOGLE_CLIENT_ID isn't set.
 */
export const GoogleSignInButton = ({ onCredential, text = 'continue_with' }) => {
  const buttonRef = useRef(null);

  const handleCredential = useCallback((response) => {
    onCredential(response.credential);
  }, [onCredential]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;

    let cancelled = false;

    const renderButton = () => {
      if (cancelled || !buttonRef.current || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredential,
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text,
        shape: 'pill',
        width: 360,
      });
    };

    if (window.google?.accounts?.id) {
      renderButton();
      return () => { cancelled = true; };
    }

    // The GIS script loads async, so poll briefly until it's ready.
    const interval = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(interval);
        renderButton();
      }
    }, 100);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [handleCredential, text]);

  if (!GOOGLE_CLIENT_ID) return null;

  return <div ref={buttonRef} className="flex justify-center" data-testid="google-signin-button" />;
};
